import React, { useState, useEffect, useRef, useCallback } from "react";
import { View, ScrollView, StyleSheet } from "react-native";
import CustomButton from "../../components/common/general/CustomButton/CustomButton";
import { DownArrow } from "../../components/svgs/common";
import CustomSpacing from "../../components/common/layout/CustomSpacing/CustomSpacing";
import CustomText from "../../components/common/general/CustomText/CustomText";
import ProcessingMessageBox from "../../components/chat/LoadingMessage/ProcessingMessageBox";
import { useFocusEffect } from "@react-navigation/native";
import { MessageBubble } from "../../components/chatNewInterface/interfaceComponents/messageBubble/MessageBubble";
import { AttachmentChipProps } from "../../components/chatNewInterface/interfaceComponents/attachmentChip/AttachmentChip";
import { LearningActivityCard } from "../../components/chatNewInterface/interfaceComponents/learningACtivityCard/LearningActivityCard";
import { GeneratedImageCard } from "../../components/chatNewInterface/interfaceComponents/generatedImageCard/GeneratedImageCard";
import { spacing, textColorStyles } from "../../styles/design-system";
import type { LearningSignal, CreatedILE, MessageItem } from "./companionMessage.types";
import type { ReadAloudControl } from "../../components/chatNewInterface/interfaceComponents/messageBubble/ReadAloudPlaybackControl";
import { ImageBlockStatus } from "../../graphql/generated/graphql";

export type { LearningSignal, CreatedILE, MessageItem };

interface MessagesContainerProps {
  messages: MessageItem[];
  isStreaming?: boolean;
  currentStreamingMessageId?: string | null;
  resetAutoScroll?: number;
  onStartEdit?: (messageId: string, messageIndex: number, text: string) => void;
  editingMessageId?: string | null;
  editingMessageDraft?: string | null;
  onChangeEditingMessageDraft?: (value: string) => void;
  onSaveEdit?: () => void;
  onCancelEdit?: () => void;
  onSwitchBranch?: (anchorMessageId: string, targetBranchId: string | null) => void;
  feedbackByMessageId?: Record<string, "up" | "down">;
  onFeedback?: (messageId: string, value: "up" | "down" | null) => void;
  onOpenLearningActivity?: (ileId: string) => void;
  streamStatusMessage?: string | null;
  showIleCreationLoader?: boolean;
  ileCreationErrorMessage?: string | null;
  getAssistantReadAloudControl?: (
    messageId: string,
    content: string,
    meters?: { streamAudioLevel: number; streamAudioLevels: number[] }
  ) => ReadAloudControl | undefined;
  streamAudioLevel?: number;
  streamAudioLevels?: number[];
}

const CONTAINER_PADDING_BOTTOM = 16;

const mapMessageToBubble = (message: Partial<MessageItem>) => ({
  role: (message?.sender === "human" ? "user" : "assistant") as "user" | "assistant",
  content: message?.content ?? "",
  timestamp: message?.created ? new Date(message.created) : new Date(),
  isArchived: false,
  selectedBranchId: message?.selectedBranchId,
  branches: message?.branches,
});

const getImageGenerationState = (message: MessageItem) => {
  const imageBlock = message.imageBlocks?.[0];
  if (!imageBlock) return null;

  if (imageBlock.status === ImageBlockStatus.Completed && imageBlock.url) {
    return {
      status: "completed" as const,
      imageUrl: imageBlock.url,
      errorMessage: null,
    };
  }
  if (imageBlock.status === ImageBlockStatus.Completed && !imageBlock.url) {
    return {
      status: "failed" as const,
      imageUrl: null,
      errorMessage: "Image generation completed without an image URL.",
    };
  }
  if (imageBlock.status === ImageBlockStatus.Failed) {
    return {
      status: "failed" as const,
      imageUrl: null,
      errorMessage: imageBlock.errorMessage ?? "Image generation failed, please try again later.",
    };
  }
  return {
    status: "loading" as const,
    imageUrl: null,
    errorMessage: null,
  };
};

const MessagesContainer = ({
  messages,
  isStreaming = false,
  currentStreamingMessageId,
  resetAutoScroll,
  onStartEdit,
  editingMessageId,
  editingMessageDraft,
  onChangeEditingMessageDraft,
  onSaveEdit,
  onCancelEdit,
  onSwitchBranch,
  feedbackByMessageId,
  onFeedback,
  onOpenLearningActivity,
  streamStatusMessage = null,
  showIleCreationLoader = false,
  ileCreationErrorMessage = null,
  getAssistantReadAloudControl,
  streamAudioLevel = 0,
  streamAudioLevels = [],
}: MessagesContainerProps) => {
  const scrollViewRef = useRef(null);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const [downButton, setDownButton] = useState(false);
  const [humanMessageOffset, setHumanMessageOffset] = useState(0);
  const [scrollViewHeight, setScrollViewHeight] = useState(undefined);
  const [parentHeight, setParentHeight] = useState(0);

  const [isUserInteracted, setIsUserInteracted] = useState(false);

  const isAtBottomRef = useRef(true);

  useFocusEffect(
    useCallback(() => {
      return () => {
        setIsUserInteracted(false);
      };
    }, [])
  );

  const handleScroll = useCallback((event) => {
    const { layoutMeasurement, contentOffset, contentSize } = event.nativeEvent;
    const isAtBottom = layoutMeasurement.height + contentOffset.y >= contentSize.height - 50;

    if (!isAtBottom) {
      setIsUserScrolling(true);
      setDownButton(true);
      setIsUserInteracted(true); // User scrolled up, stop auto-scrolling
    } else {
      setIsUserScrolling(false);
      setDownButton(false);
    }

    isAtBottomRef.current = isAtBottom;
  }, []);

  const scrollToBottom = (animated = false) => {
    if (scrollViewRef.current) {
      scrollViewRef.current.scrollToEnd({ animated });
    }
  };

  const scrollToBottomButton = () => {
    setDownButton(false);
    scrollToBottom(true);
  };

  useEffect(() => {
    if (scrollViewRef.current) {
      scrollViewRef.current.setAttribute("data-sensitive", "true");
    }
  }, []);

  // Reset user interaction and scroll height when resetAutoScroll changes (e.g., when new message is submitted or chat switches)
  useEffect(() => {
    if (resetAutoScroll !== undefined) {
      setIsUserInteracted(false);
      setScrollViewHeight(undefined);
      setHumanMessageOffset(0);
    }
  }, [resetAutoScroll]);

  // Reset scroll height when chat switch
  useEffect(() => {
    if (messages.length === 0) {
      setScrollViewHeight(undefined);
      setHumanMessageOffset(0);
    }
  }, [messages.length]);

  useEffect(() => {
    if (!scrollViewRef.current) return;

    // Only auto-scroll when the user has not manually scrolled up.
    // This prevents branch switches (which change layout/offsets) from forcing a jump to the bottom
    // when the user is reviewing earlier messages.
    if (!isUserInteracted) {
      scrollToBottom();
    }
  }, [humanMessageOffset, isUserInteracted]);

  useEffect(() => {
    if (!messages) return;
    if (messages.length === 0) return;

    if (!isUserInteracted) {
      scrollToBottom();
    }
  }, [messages]);

  const handleOnLayout = (event, item, array) => {
    const isLastHumanMessage = array.findLast((item) => item.sender === "human")?.id === item.id;
    if (isLastHumanMessage) {
      const humanMessageOffsetFromTop = event.nativeEvent.layout.y;
      setHumanMessageOffset(humanMessageOffsetFromTop);
      setScrollViewHeight(humanMessageOffsetFromTop + parentHeight - CONTAINER_PADDING_BOTTOM);
    }
  };

  const streamingMessage = currentStreamingMessageId
    ? messages.find((message) => message.id === currentStreamingMessageId)
    : undefined;
  const shouldShowProcessingFallback = isStreaming && (!streamingMessage || !streamingMessage.content);

  // Filter out empty assistant messages that are currently streaming,
  // but keep them if they have an image generation state (loading card must remain visible).
  const visibleMessages = messages?.filter((item) => {
    const imageGeneration = getImageGenerationState(item);
    if (
      isStreaming &&
      item.id === currentStreamingMessageId &&
      item.sender === "ai" &&
      !item.content?.trim() &&
      !imageGeneration
    ) {
      return false;
    }
    return true;
  });

  const statusToShow = streamStatusMessage;
  const lastVisibleMessage = visibleMessages?.[visibleMessages.length - 1];
  const lastVisibleIsAssistant = lastVisibleMessage?.sender === "ai";

  return (
    <View
      style={[styles.container, { paddingBottom: CONTAINER_PADDING_BOTTOM }]}
      onLayout={(event) => setParentHeight(event.nativeEvent.layout.height)}>
      <ScrollView
        ref={scrollViewRef}
        style={styles.scrollView}
        onScroll={handleScroll}
        scrollEventThrottle={16}
        maintainVisibleContentPosition={{
          minIndexForVisible: 0,
          autoscrollToTopThreshold: 10,
        }}
        showsVerticalScrollIndicator={true}
        contentContainerStyle={{ height: scrollViewHeight }}
        onContentSizeChange={() => {
          if (!isUserInteracted) {
            scrollToBottom();
          }
        }}
        tabIndex={0}>
        <View style={styles.view}>
          {visibleMessages?.map((item, index, array) => {
            const attachmentItems: AttachmentChipProps[] = [];

            for (const todo of item.todos ?? []) {
              attachmentItems.push({
                type: "task",
                name: todo.name,
                duedate: todo.date,
              });
            }

            for (const assignment of item.assignments ?? []) {
              attachmentItems.push({
                type: "assignment",
                name: assignment.name,
                duedate: assignment.due_date,
              });
            }

            for (const file of item.files ?? []) {
              attachmentItems.push({
                type: "file",
                name: file.fileName,
              });
            }

            const resolvedCreatedILE = item.createdILE ?? item.metadata?.createdILE;
            const showCreatedILE = item.sender === "ai" && resolvedCreatedILE;
            const isLastAssistantMessage = index === array.length - 1 && item.sender === "ai";
            const isStreamingCurrentMessage =
              item.sender === "ai" && item.id === currentStreamingMessageId && isStreaming;
            const readAloudControl =
              item.sender === "ai" && item.id && getAssistantReadAloudControl
                ? getAssistantReadAloudControl(item.id, item.content ?? "", {
                    streamAudioLevel,
                    streamAudioLevels,
                  })
                : undefined;
            const shouldHideReadAloudControl = isStreamingCurrentMessage;
            const showStatusInBubble = isLastAssistantMessage && statusToShow && !ileCreationErrorMessage;
            const showIleCardLoading =
              isLastAssistantMessage && showIleCreationLoader && !resolvedCreatedILE && !ileCreationErrorMessage;
            const showIleCreationError = isLastAssistantMessage && ileCreationErrorMessage;
            const imageGeneration = item.sender === "ai" ? getImageGenerationState(item) : null;

            return (
              <View
                onLayout={(event) => {
                  handleOnLayout(event, item, array);
                }}
                key={item.id || index}>
                <MessageBubble
                  {...mapMessageToBubble(item)}
                  taskAttachmentChips={attachmentItems}
                  onEdit={
                    onStartEdit && !isStreaming && item.sender === "human" && item.id
                      ? () => onStartEdit(item.id!, item.index ?? index, item.content ?? "")
                      : undefined
                  }
                  isBeingEdited={editingMessageId === item.id}
                  onCancelEdit={onCancelEdit}
                  editValue={editingMessageId === item.id ? (editingMessageDraft ?? item.content ?? "") : undefined}
                  onChangeEditValue={editingMessageId === item.id ? onChangeEditingMessageDraft : undefined}
                  onSaveEdit={editingMessageId === item.id ? onSaveEdit : undefined}
                  onSwitchBranch={onSwitchBranch}
                  branches={item.branches}
                  selectedBranchId={item.selectedBranchId}
                  feedback={item.sender === "ai" ? feedbackByMessageId?.[item.id] : undefined}
                  onFeedback={
                    item.sender === "ai" && item.id && onFeedback ? (value) => onFeedback(item.id, value) : undefined
                  }
                  hideReadAloudControl={shouldHideReadAloudControl}
                  readAloudControl={readAloudControl}
                  betweenBubbleAndActions={
                    showStatusInBubble ||
                    showCreatedILE ||
                    showIleCardLoading ||
                    showIleCreationError ||
                    imageGeneration ? (
                      <View style={styles.betweenBubbleContent}>
                        {showStatusInBubble ? (
                          <CustomText
                            useTranslationText={false}
                            text={statusToShow}
                            size="s"
                            style={styles.statusMessageText}
                          />
                        ) : null}
                        {showIleCreationError ? (
                          <CustomText
                            useTranslationText={false}
                            text={ileCreationErrorMessage ?? ""}
                            size="s"
                            style={styles.ileCreationErrorText}
                          />
                        ) : null}
                        {showCreatedILE && resolvedCreatedILE ? (
                          <View style={styles.ileCardRow}>
                            <LearningActivityCard
                              topic={resolvedCreatedILE.title}
                              description={resolvedCreatedILE.description ?? undefined}
                              goals={
                                resolvedCreatedILE.objectives?.map((objective) => objective?.objective ?? "") ?? []
                              }
                              onPress={() =>
                                resolvedCreatedILE.id ? onOpenLearningActivity?.(resolvedCreatedILE.id) : undefined
                              }
                              isGeneratingActivity={false}
                            />
                          </View>
                        ) : null}
                        {showIleCardLoading ? (
                          <View style={styles.ileCardRow}>
                            <LearningActivityCard
                              topic="Creating your learning activity..."
                              description={undefined}
                              goals={[]}
                              onPress={() => {}}
                              isGeneratingActivity={true}
                            />
                          </View>
                        ) : null}
                        {imageGeneration ? (
                          <GeneratedImageCard
                            status={imageGeneration.status}
                            imageUrl={imageGeneration.imageUrl}
                            errorMessage={imageGeneration.errorMessage}
                          />
                        ) : null}
                      </View>
                    ) : undefined
                  }
                />
              </View>
            );
          })}
          {shouldShowProcessingFallback && (
            <View>
              <CustomSpacing type="vertical" size="s" />
              <ProcessingMessageBox />
            </View>
          )}
          {statusToShow && !lastVisibleIsAssistant ? (
            <View style={styles.statusMessageRow}>
              <CustomSpacing type="vertical" size="s" />
              <CustomText useTranslationText={false} text={statusToShow} size="s" style={styles.statusMessageText} />
            </View>
          ) : null}
        </View>
      </ScrollView>
      {downButton && (
        <View style={styles.buttonContainer}>
          <CustomButton
            styleType="primary"
            style={styles.button}
            leftIcon={<DownArrow />}
            onPress={scrollToBottomButton}
            rightIcon={undefined}
            text={undefined}
            textProps={undefined}
            textStyle={undefined}
            disabled={false}
          />
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollView: {
    height: 20,
    paddingHorizontal: 10,
    zIndex: 0,
  },
  view: {
    width: "100%",
    alignSelf: "center",
    maxWidth: 768,
    paddingHorizontal: 10,
  },
  button: {
    justifyContent: "center",
    position: "absolute",
    alignItems: "center",
    bottom: 10,
    width: 30,
    height: 30,
    borderRadius: 25,
    zIndex: 5,
  },
  buttonContainer: {
    alignItems: "center",
  },
  betweenBubbleContent: {
    marginTop: spacing.xs,
    width: "100%",
  },
  ileCardRow: {
    marginTop: spacing.sm,
    marginLeft: 40,
    width: "100%",
  },
  statusMessageRow: {
    marginLeft: 40,
    paddingVertical: spacing.xs,
  },
  statusMessageText: {
    fontStyle: "italic",
    opacity: 0.8,
  },
  ileCreationErrorText: {
    ...textColorStyles.textErrorDark,
    fontStyle: "italic",
    marginTop: spacing.xs,
  },
});

export default MessagesContainer;
